import { Portal } from "@gorhom/portal";
import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { ScrollView as RNScrollView } from "react-native";
import { generatePath, useNavigate, useParams } from "react-router";
import { AnchorProduct } from "../../../../domain/projections/AnchorProduct";
import { ProductPreview } from "../../../../domain/projections/ProductPreview";
import { useGetAnchorProducts } from "../../behaviors/useGetAnchorProducts";
import { useGetCompleteYourLookLooks } from "../../behaviors/useGetCompleteYourLookLooks";
import { useGetExperiments } from "../../behaviors/useGetExperiments";
import { useHardwareBackPress } from "../../behaviors/useHardwareBackPress";
import { useUserHasPurchase } from "../../behaviors/useUserHasPurchase";
import { AnchorGarmentSelector } from "../../components/templates/anchorGarmentSelector/AnchorGarmentSelector";
import { CompleteYourLook as CompleteYourLookTemplate } from "../../components/templates/completeYourLook/CompleteYourLook";
import { UnlockYourCompleteYourLookBanner } from "../../components/templates/completeYourLook/unlockYourCompleteYourLookBanner/UnlockYourCompleteYourLookBanner";
import { UpdateYourProfileBanner } from "../../components/updateYourProfileBanner/UpdateYourProfileBanner";
import { InViewProvider } from "../../hooks/useInView";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { Routes } from "../../routing/routes";
import { STORAGE_KEY_HOME_SCROLL } from "../../shared/util/storageKeys";
import { List, TrackingPage, TrackingView } from "../../tracking/tracking";
import { useTrackCategoriesSelected } from "../../tracking/useTrackCategoriesSelected";
import { useTrackItemSelected } from "../../tracking/useTrackItemSelected";
import { useTrackPageView } from "../../tracking/useTrackPageView";
import { useTrackShopButtonClicked } from "../../tracking/useTrackShopButtonClicked";
import { useTrackViewItemList } from "../../tracking/useTrackViewItemList";
import { OFFERING_HEADER_PORTAL_HOST } from "../offeringLayout/OfferingLayout";
import { DEFAULT_SCROLL_VALUE } from "./CompleteYourLook.definition";

interface CompleteYourLookProps {
  readonly onBack: () => void;
  readonly onBackToStyleProfile: () => void;
}

const CompleteYourLook: FC<CompleteYourLookProps> = ({ onBack, onBackToStyleProfile }) => {
  const {
    session: { auth, user },
    store,
    basePath,
    tradename,
    apiUrl,
  } = useStaticInfo();
  const navigate = useNavigate();
  const params = useParams();
  const anchorProductVariantIdFromRoute = params.anchorProductVariantId;

  const trackingCommonParams = useMemo(
    () => ({
      country: user.country,
      segment: user.segment,
      userId: user.id,
      tradename,
    }),
    [tradename, user.country, user.id, user.segment],
  );
  useTrackPageView({
    ...trackingCommonParams,
    page: TrackingPage.COMPLETE_YOUR_LOOK,
    view: TrackingView.LOOK_IN_HOME,
  });
  const trackCategoriesSelected = useTrackCategoriesSelected(trackingCommonParams);
  const trackShopButtonClicked = useTrackShopButtonClicked(trackingCommonParams);
  const trackItemSelected = useTrackItemSelected(trackingCommonParams);
  const trackViewItemList = useTrackViewItemList(trackingCommonParams);

  const anchorProductVariants = useGetAnchorProducts({
    ...trackingCommonParams,
    auth,
    apiUrl,
  });
  const { looks, isLoading } = useGetCompleteYourLookLooks(anchorProductVariantIdFromRoute);
  const { experiments } = useGetExperiments();
  const { hasPurchase, isLoading: isLoadingHasPurchase } = useUserHasPurchase();

  const scrollViewRef = useRef<RNScrollView>(null);
  const scrollValueRef = useRef<number>(DEFAULT_SCROLL_VALUE);

  const EmptyState = useMemo(
    () =>
      hasPurchase ? (
        <UpdateYourProfileBanner onBackToStyleProfile={onBackToStyleProfile} />
      ) : (
        <UnlockYourCompleteYourLookBanner onBack={onBack} />
      ),

    [hasPurchase, onBack, onBackToStyleProfile],
  );

  const saveScrollValueInStore = useCallback(
    async (value = 0) => {
      await store.set(STORAGE_KEY_HOME_SCROLL, value < 0 ? 0 : value);
    },
    [store],
  );

  const handleOnCrossNavigation = useCallback(
    (buttonId: string) => {
      trackShopButtonClicked({ buttonId });

      trackCategoriesSelected();

      saveScrollValueInStore(DEFAULT_SCROLL_VALUE);

      navigate(`${basePath}/${Routes.CATEGORIES}`);
    },
    [basePath, navigate, saveScrollValueInStore, trackCategoriesSelected, trackShopButtonClicked],
  );

  const handleOnPressLook = useCallback(
    ({ lookId, product }: { lookId: string; product: ProductPreview }) => {
      saveScrollValueInStore(scrollValueRef.current);

      trackItemSelected({
        list: List.COMPLETE_YOUR_LOOK,
        productPreview: product,
        lookId,
      });
    },
    [saveScrollValueInStore, trackItemSelected],
  );

  const handleOnScroll = useCallback((coords: { X: number; Y: number }) => {
    scrollValueRef.current = coords.Y;
  }, []);

  const handleOnLayoutLooks = useCallback(async () => {
    if (looks && !isLoading) {
      trackViewItemList({
        experiments,
        list: List.COMPLETE_YOUR_LOOK,
        looks,
      });
    }

    const storedScroll = await store.get(STORAGE_KEY_HOME_SCROLL);

    if (Number(storedScroll) >= 0 && scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ x: 0, y: Number(storedScroll), animated: false });
    }
  }, [experiments, isLoading, looks, store, trackViewItemList]);

  const showAnchorSelector = Boolean(anchorProductVariants !== undefined && anchorProductVariants.length >= 2);

  const navigateToLooksByAnchorItem = useCallback(
    (anchorProductVariantId: string) =>
      navigate(
        generatePath(`${basePath}/${Routes.LOOKS_BY_ANCHOR_GARMENT}`, {
          anchorProductVariantId,
        }),
        { replace: true },
      ),
    [navigate, basePath],
  );

  useEffect(() => {
    if (showAnchorSelector && !anchorProductVariantIdFromRoute) {
      const firstAnchorProduct = (anchorProductVariants as AnchorProduct[])[0];

      if (firstAnchorProduct) {
        navigateToLooksByAnchorItem(firstAnchorProduct.id);
      }
    }
  }, [
    anchorProductVariants,
    anchorProductVariantIdFromRoute,
    showAnchorSelector,
    navigate,
    basePath,
    navigateToLooksByAnchorItem,
  ]);

  useHardwareBackPress(onBack);

  return (
    <>
      {showAnchorSelector ? (
        <Portal hostName={OFFERING_HEADER_PORTAL_HOST}>
          <AnchorGarmentSelector
            productVariants={anchorProductVariants as AnchorProduct[]}
            selectedAnchorProductVariantId={
              anchorProductVariantIdFromRoute || ((anchorProductVariants as AnchorProduct[])[0]?.id as string)
            }
            onPress={(itemId) => {
              navigateToLooksByAnchorItem(itemId);
              saveScrollValueInStore(DEFAULT_SCROLL_VALUE);
            }}
          />
        </Portal>
      ) : null}
      <InViewProvider key={`complete-your-look-${anchorProductVariantIdFromRoute}`}>
        <CompleteYourLookTemplate
          emptyState={!looks?.length ? EmptyState : undefined}
          isLoading={isLoading || isLoadingHasPurchase}
          looks={looks}
          scrollViewRef={scrollViewRef}
          showHeader={!showAnchorSelector}
          onBack={onBack}
          onCrossNavigation={handleOnCrossNavigation}
          onLayoutLooks={handleOnLayoutLooks}
          onPressLook={handleOnPressLook}
          onScroll={handleOnScroll}
        />
      </InViewProvider>
    </>
  );
};

export { CompleteYourLook };
